const committee_type_config = {
  Board: { label: 'BOARD', icon: 'bi-building', icon_tone: 'teal' },
  EXCO: { label: 'EXCO', icon: 'bi-briefcase', icon_tone: 'teal' },
  'Audit committee': { label: 'AUDIT COMMITTEE', icon: 'bi-clipboard-check', icon_tone: 'teal' },
  'Risk committee': { label: 'RISK COMMITTEE', icon: 'bi-exclamation-triangle', icon_tone: 'teal' },
  'Procurement committee': { label: 'PROCUREMENT COMMITTEE', icon: 'bi-cart3', icon_tone: 'teal' },
  'ICT steering committee': { label: 'ICT STEERING COMMITTEE', icon: 'bi-laptop', icon_tone: 'orange' }
};

const committee_status_classes = {
  Active: 'bg-success-subtle text-success-emphasis',
  Forming: 'bg-warning-subtle text-warning-emphasis',
  Dormant: 'bg-secondary-subtle text-secondary-emphasis'
};

const action_status_classes = {
  Open: 'bg-warning-subtle text-warning-emphasis',
  'In progress': 'bg-warning-subtle text-warning-emphasis',
  Done: 'bg-success-subtle text-success-emphasis',
  Overdue: 'bg-danger-subtle text-danger-emphasis'
};

const accountability_status_classes = {
  'On track': 'bg-success-subtle text-success-emphasis',
  Attention: 'bg-warning-subtle text-warning-emphasis',
  Gap: 'bg-danger-subtle text-danger-emphasis'
};

const calendar_status_classes = {
  Scheduled: 'bg-success-subtle text-success-emphasis',
  Due: 'bg-warning-subtle text-warning-emphasis',
  Overdue: 'bg-danger-subtle text-danger-emphasis',
  Done: 'bg-success-subtle text-success-emphasis'
};

const consequence_status_classes = {
  Open: 'bg-danger-subtle text-danger-emphasis',
  'Under review': 'bg-warning-subtle text-warning-emphasis',
  'Action taken': 'bg-success-subtle text-success-emphasis',
  Closed: 'bg-success-subtle text-success-emphasis'
};

const decision_status_classes = {
  Passed: 'bg-success-subtle text-success-emphasis',
  'Awaiting signature': 'bg-danger-subtle text-danger-emphasis',
  Deferred: 'bg-warning-subtle text-warning-emphasis',
  Rejected: 'bg-danger-subtle text-danger-emphasis'
};

function slugify(value) {
  const slugified_value = String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slugified_value;
}

function value_or_default(value, fallback_value) {
  return value || fallback_value;
}

export function create_empty_committee_form() {
  const empty_committee_form = { name: '', type: 'Board', chairperson: '', members: '', meeting_cadence: 'Monthly', quorum: '', status: 'Active', mandate: '' };
  return empty_committee_form;
}
export function create_empty_decision_form() {
  const empty_decision_form = { decision: '', decided_in: 'Board', approved_by: '', accountable_owner: '', under_authority: '', authorises: '', linked_risk: '', linked_policy: '', linked_evidence: '', meeting_date: '', status: 'Passed', note: '' };
  return empty_decision_form;
}

export function create_empty_action_form() {
  const empty_action_form = { action: '', owner: '', raised_in: 'Board', due: '', status: 'Open' };
  return empty_action_form;
}

export function create_empty_accountability_form() {
  const empty_accountability_form = { area: '', accountable: '', responsible: '', oversight: '', proof: '', status: 'On track' };
  return empty_accountability_form;
}

export function create_empty_calendar_form() {
  const empty_calendar_form = { event: '', type: 'Board meeting', date: '', owner: '', status: 'Scheduled', icon: 'bi-building' };
  return empty_calendar_form;
}

export function create_empty_consequence_form() {
  const empty_consequence_form = { breach: '', responsible_party: '', severity: 'Medium', action_taken: '', corrective_step: '', status: 'Open' };
  return empty_consequence_form;
}

function resolve_committee_type_config(type) {
  const type_config = Reflect.get(committee_type_config, type) || committee_type_config.Board;
  return type_config;
}

function resolve_committee_id(id, title, sequence) {
  if (id) {
    const existing_id = id;
    return existing_id;
  }
  const sequence_value = sequence || 1;
  const generated_id = `${slugify(title)}-${sequence_value}`;
  return generated_id;
}

function resolve_committee_status_class(status) {
  const status_class =
    Reflect.get(committee_status_classes, status) ??
    committee_status_classes.Dormant;
  return status_class;
}

function build_committee_footer(form) {
  const committee_footer = [
    { icon: 'bi-person', text: value_or_default(form.chairperson, 'TBC') },
    { icon: 'bi-people', text: value_or_default(form.members, 'TBC') },
    { icon: 'bi-calendar3', text: value_or_default(form.meeting_cadence, 'Monthly') },
    { icon: 'bi-check2-square', text: form.quorum ? `Quorum ${form.quorum}` : 'Quorum TBC' }
  ];
  return committee_footer;
}

function normalize_committee_form(form) {
  const normalized_committee_form = {
    type: value_or_default(form.type, 'Board'),
    status: value_or_default(form.status, 'Active'),
    title: value_or_default(form.name, 'Unnamed committee'),
    chairperson: value_or_default(form.chairperson, ''),
    members: value_or_default(form.members, ''),
    meeting_cadence: value_or_default(form.meeting_cadence, 'Monthly'),
    quorum: value_or_default(form.quorum, ''),
    mandate: value_or_default(form.mandate, '')
  };
  return normalized_committee_form;
}

export function create_committee_card(form, { id = null, sequence = null } = {}) {
  const normalized_form = normalize_committee_form(form);
  const type_config = resolve_committee_type_config(normalized_form.type);
  const generated_id = resolve_committee_id(id, normalized_form.title, sequence);
  const status_class = resolve_committee_status_class(normalized_form.status);
  const footer = build_committee_footer(normalized_form);
  const committee_model = {
    id: generated_id,
    title: normalized_form.title,
    type: normalized_form.type,
    label: type_config.label,
    status: normalized_form.status,
    status_class,
    icon: type_config.icon,
    icon_tone: type_config.icon_tone,
    chairperson: normalized_form.chairperson,
    members: normalized_form.members,
    meeting_cadence: normalized_form.meeting_cadence,
    quorum: normalized_form.quorum,
    mandate: normalized_form.mandate,
    copy: normalized_form.mandate,
    footer
  };

  return committee_model;
}

export function clone_committee_cards(cards) {
  const cloned_cards = cards.map((card) => ({ ...card, footer: card.footer.map((meta) => ({ ...meta })) }));
  return cloned_cards;
}

function resolve_action_status_class(status_value) {
  const action_status_class = Reflect.get(action_status_classes, status_value) || action_status_classes.Open;
  return action_status_class;
}

function resolve_accountability_status_class(status_value) {
  const accountability_status_class = Reflect.get(accountability_status_classes, status_value) || Reflect.get(accountability_status_classes, 'On track');
  return accountability_status_class;
}

function resolve_calendar_status_class(status_value) {
  const calendar_status_class = Reflect.get(calendar_status_classes, status_value) || calendar_status_classes.Scheduled;
  return calendar_status_class;
}

function resolve_consequence_status_class(status_value) {
  const consequence_status_class = Reflect.get(consequence_status_classes, status_value) || consequence_status_classes.Open;
  return consequence_status_class;
}

function resolve_decision_status_class(status_value) {
  const decision_status_class = Reflect.get(decision_status_classes, status_value) || decision_status_classes.Passed;
  return decision_status_class;
}

function normalized_calendar_form(form) {
  const calendar_form = {
    event: value_or_default(form.event, 'Untitled event'),
    type: value_or_default(form.type, 'Board meeting'),
    date: value_or_default(form.date, '-'),
    owner: value_or_default(form.owner, 'TBC'),
    icon: value_or_default(form.icon, 'bi-building')
  };
  return calendar_form;
}

function normalized_consequence_form(form) {
  const consequence_form = {
    breach: value_or_default(form.breach, 'Untitled breach'),
    responsible_party: value_or_default(form.responsible_party, 'TBC'),
    severity: value_or_default(form.severity, 'Medium'),
    action_taken: value_or_default(form.action_taken, 'TBC'),
    corrective_step: value_or_default(form.corrective_step, 'TBC')
  };
  return consequence_form;
}

export function build_action_row(form, id) {
  const status_value = form.status || 'Open';
  const action_row = {
    id,
    action: value_or_default(form.action, 'Untitled action'),
    owner: value_or_default(form.owner, 'TBC'),
    raised_in: value_or_default(form.raised_in, 'Board'),
    due: value_or_default(form.due, '-'),
    status: status_value,
    status_class: resolve_action_status_class(status_value)
  };
  return action_row;
}

export function build_accountability_row(form, id) {
  const status_value = form.status || 'On track';
  const accountability_row = {
    id,
    area: value_or_default(form.area, 'Untitled area'),
    accountable: value_or_default(form.accountable, 'TBC'),
    responsible: value_or_default(form.responsible, 'TBC'),
    oversight: value_or_default(form.oversight, 'TBC'),
    proof: value_or_default(form.proof, 'TBC'),
    status: status_value,
    status_class: resolve_accountability_status_class(status_value)
  };
  return accountability_row;
}

export function build_calendar_row(form, id) {
  const status_value = form.status || 'Scheduled';
  const normalized_form = normalized_calendar_form(form);
  const calendar_row = {
    id,
    event: normalized_form.event,
    type: normalized_form.type,
    date: normalized_form.date,
    owner: normalized_form.owner,
    status: status_value,
    icon: normalized_form.icon,
    status_class: resolve_calendar_status_class(status_value)
  };
  return calendar_row;
}

export function build_consequence_row(form, id) {
  const status_value = form.status || 'Open';
  const normalized_form = normalized_consequence_form(form);
  const consequence_row = {
    id,
    breach: normalized_form.breach,
    responsible_party: normalized_form.responsible_party,
    severity: normalized_form.severity,
    action_taken: normalized_form.action_taken,
    corrective_step: normalized_form.corrective_step,
    status: status_value,
    status_class: resolve_consequence_status_class(status_value)
  };
  return consequence_row;
}

export function build_decision_row(form, { id, ref }) {
  const status_value = form.status || 'Passed';
  const decision_view_model = {
    id,
    ref,
    decision: value_or_default(form.decision, 'Untitled decision'),
    note: value_or_default(form.note, ''),
    approved_by: value_or_default(form.decided_in, 'Board'),
    decided_in: value_or_default(form.decided_in, 'Board'),
    accountable_owner: value_or_default(form.accountable_owner, ''),
    linked_risk: value_or_default(form.linked_risk, '-'),
    authorises: value_or_default(form.authorises || form.under_authority, '-'),
    under_authority: value_or_default(form.under_authority, ''),
    linked_policy: value_or_default(form.linked_policy, ''),
    linked_evidence: value_or_default(form.linked_evidence, ''),
    meeting_date: value_or_default(form.meeting_date, '-'),
    status: status_value,
    status_class: resolve_decision_status_class(status_value),
    highlight: false
  };

  return decision_view_model;
}
